import { AppUser, CompanyInfo, ContractPaymentRecord, ContractedCompany, CurrencyCode, ExchangeRateInfo, InventoryItem, InventoryLot, MySqlConfig, MySqlTestResult, ServicePart, ServiceTicket, ServiceType, StockMovement, TicketStatus } from '../types';
import {
  defaultCompanyInfo,
  defaultDeviceTypes,
  defaultDeviceBrands,
  defaultInventoryCategories,
  initialInventoryItems,
  initialStockMovements,
  initialTickets,
} from '../data/initialData';

const TICKETS_KEY = 'ts_tickets_v1';
const COMPANY_KEY = 'ts_company_v1';
const DEVICE_TYPES_KEY = 'ts_device_types_v1';
const DEVICE_BRANDS_KEY = 'ts_device_brands_v1';
const INVENTORY_KEY = 'ts_inventory_v1';
const INVENTORY_CATEGORIES_KEY = 'ts_inventory_categories_v1';
const STOCK_MOVEMENTS_KEY = 'ts_stock_movements_v1';
const USERS_KEY = 'ts_users_v1';
const CURRENT_USER_KEY = 'ts_current_user_v1';
const AUTH_SESSION_VERSION_KEY = 'ts_auth_session_version';
const EXCHANGE_RATE_KEY = 'ts_exchange_rate_v1';
const CONTRACTED_COMPANIES_KEY = 'ts_contracted_companies_v1';
const DATABASE_INSTANCE_KEY = 'ts_database_instance_v1';

export const defaultExchangeRateInfo: ExchangeRateInfo = {
  rate: 47.96,
  buyingRate: 47.88,
  sellingRate: 47.96,
  source: 'TCMB',
  updatedAt: new Date().toISOString(),
  manualOverride: false,
};

export function getStoredExchangeRate(): ExchangeRateInfo {
  try {
    const raw = localStorage.getItem(EXCHANGE_RATE_KEY);
    if (!raw) {
      localStorage.setItem(EXCHANGE_RATE_KEY, JSON.stringify(defaultExchangeRateInfo));
      return defaultExchangeRateInfo;
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.rate === 'number' && parsed.rate > 0) {
      return parsed;
    }
    return defaultExchangeRateInfo;
  } catch (e) {
    console.error('Error reading exchange rate:', e);
    return defaultExchangeRateInfo;
  }
}

export function saveStoredExchangeRate(rateInfo: ExchangeRateInfo): void {
  try {
    localStorage.setItem(EXCHANGE_RATE_KEY, JSON.stringify(rateInfo));
    // Notify any active React components in this window
    window.dispatchEvent(new CustomEvent('ts_exchange_rate_updated', { detail: rateInfo }));
    
    fetch('/api.php?action=exchange-rate', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rateInfo),
    }).catch(() => {});
  } catch (err) {
    console.error('Error saving exchange rate:', err);
  }
}

export async function fetchLiveExchangeRate(forceRefresh = false): Promise<ExchangeRateInfo> {
  // Primary: PHP/MySQL backend
  // Try PHP /api.php?action=exchange-rate
  try {
    const phpRes = await fetch(`/api.php?action=exchange-rate${forceRefresh ? '&refresh=1' : ''}`, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (phpRes.ok) {
      const phpData = await phpRes.json();
      if (phpData && phpData.success && phpData.exchangeRate && typeof phpData.exchangeRate.rate === 'number') {
        saveStoredExchangeRate(phpData.exchangeRate);
        return phpData.exchangeRate;
      }
    }
  } catch (_) {}

  // 3. Client-side fallback API (Open Exchange Rates)
  try {
    const fbRes = await fetch('https://open.er-api.com/v6/latest/USD', {
      signal: AbortSignal.timeout(5000),
    });
    if (fbRes.ok) {
      const fbData = await fbRes.json();
      if (fbData && fbData.rates && typeof fbData.rates.TRY === 'number') {
        const val = Math.round(fbData.rates.TRY * 100) / 100;
        const fresh: ExchangeRateInfo = {
          rate: val,
          buyingRate: val,
          sellingRate: val,
          source: 'FALLBACK',
          updatedAt: new Date().toISOString(),
          manualOverride: false,
        };
        saveStoredExchangeRate(fresh);
        return fresh;
      }
    }
  } catch (_) {}

  return getStoredExchangeRate();
}

export function getDefaultUsers(companyInfo?: CompanyInfo): AppUser[] {
  const adminUser = companyInfo?.adminUsername || 'admin';
  return [
    {
      id: 'usr_admin',
      username: adminUser,
      password: '',
      name: 'Sistem Yöneticisi',
      role: 'admin',
      phone: companyInfo?.phone || '',
      isActive: true,
      createdAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'usr_tech1',
      username: 'teknisyen',
      password: '',
      name: 'Teknisyen',
      role: 'technician',
      phone: '',
      isActive: true,
      createdAt: '2026-01-01T00:00:00.000Z',
    },
  ];
}

export function getStoredUsers(): AppUser[] {
  try {
    const raw = localStorage.getItem(USERS_KEY);
    const company = getStoredCompanyInfo();
    const defaults = getDefaultUsers(company);

    if (!raw) {
      localStorage.setItem(USERS_KEY, JSON.stringify(defaults));
      return defaults;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      localStorage.setItem(USERS_KEY, JSON.stringify(defaults));
      return defaults;
    }

    // Ensure master admin is always present and matches companyInfo admin credentials
    const info = getStoredCompanyInfo();
    const masterUsername = (info.adminUsername || 'admin').toLowerCase().trim();
    const hasAdmin = parsed.some(u => u.role === 'admin');
    if (!hasAdmin) {
      parsed.unshift(defaults[0]);
      localStorage.setItem(USERS_KEY, JSON.stringify(parsed));
    } else {
      // Ensure master admin user is guaranteed to have isActive: true and valid encrypted passwords
      let modified = false;
      parsed.forEach(u => {
        const isMaster = u.id === 'usr_admin' || u.username.toLowerCase().trim() === masterUsername || u.username.toLowerCase().trim() === 'admin';
        if (isMaster && u.isActive === false) {
          u.isActive = true;
          modified = true;
        }
        if (u.password) {
          u.password = '';
          modified = true;
        }
      });
      if (modified) {
        localStorage.setItem(USERS_KEY, JSON.stringify(parsed));
      }
    }
    return parsed;
  } catch (err) {
    console.error('Error loading users from storage:', err);
    return getDefaultUsers();
  }
}

export function saveUsers(users: AppUser[]): void {
  try {
    const safeUsers = users.map(({ password, ...u }) => ({ ...u, password: '' } as AppUser));
    localStorage.setItem(USERS_KEY, JSON.stringify(safeUsers));

    // Password is sent only once to the same-origin PHP endpoint; server stores a one-way hash.
    syncTrackedEntity('users', { users }).catch(() => {});

    // Send to Node backend
    fetch('/api/data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ users }),
    }).catch(() => {});
  } catch (err) {
    console.error('Error saving users:', err);
  }
}

export function getCurrentUser(): AppUser | null {
  try {
    const raw = localStorage.getItem(CURRENT_USER_KEY) || sessionStorage.getItem(CURRENT_USER_KEY);
    if (!raw) {
      // Default to admin if authenticated
      const hasAuth = sessionStorage.getItem('ts_auth') === 'true' || localStorage.getItem('ts_auth_remember') === 'true';
      if (hasAuth) {
        const users = getStoredUsers();
        return users.find(u => u.role === 'admin') || users[0] || null;
      }
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setCurrentUser(user: AppUser | null): void {
  try {
    if (user) {
      const serialized = JSON.stringify(user);
      localStorage.setItem(CURRENT_USER_KEY, serialized);
      sessionStorage.setItem(CURRENT_USER_KEY, serialized);
    } else {
      localStorage.removeItem(CURRENT_USER_KEY);
      sessionStorage.removeItem(CURRENT_USER_KEY);
    }
  } catch (err) {
    console.error('Error saving current user:', err);
  }
}

export function getCurrentAuthVersion(): string {
  try {
    return sessionStorage.getItem(AUTH_SESSION_VERSION_KEY) || localStorage.getItem(AUTH_SESSION_VERSION_KEY) || '';
  } catch {
    return '';
  }
}

export function setCurrentAuthVersion(version: string): void {
  try {
    sessionStorage.setItem(AUTH_SESSION_VERSION_KEY, version);
    localStorage.setItem(AUTH_SESSION_VERSION_KEY, version);
  } catch (err) {
    console.error('Error setting auth session version:', err);
  }
}

export function clearAuthSession(): void {
  try {
    sessionStorage.removeItem('ts_auth');
    sessionStorage.removeItem(AUTH_SESSION_VERSION_KEY);
    sessionStorage.removeItem(CURRENT_USER_KEY);
    localStorage.removeItem('ts_auth_remember');
    localStorage.removeItem(AUTH_SESSION_VERSION_KEY);
    localStorage.removeItem(CURRENT_USER_KEY);
  } catch (err) {
    console.error('Error clearing auth session:', err);
  }
}

export function clearSensitiveLocalCache(): void {
  try {
    [
      TICKETS_KEY,
      COMPANY_KEY,
      DEVICE_TYPES_KEY,
      DEVICE_BRANDS_KEY,
      INVENTORY_KEY,
      INVENTORY_CATEGORIES_KEY,
      STOCK_MOVEMENTS_KEY,
      USERS_KEY,
      CONTRACTED_COMPANIES_KEY,
      DATABASE_INSTANCE_KEY,
      'service_custom_label_config_v2',
      'tekservis_sms_settings',
      'tekservis_sms_logs',
    ].forEach(key => localStorage.removeItem(key));
  } catch (err) {
    console.error('Error clearing local application cache:', err);
  }
}

export function validateCurrentSession(
  serverData: { company?: CompanyInfo; users?: AppUser[] } | null
): { isValid: boolean; reason?: string } {
  const hasActiveAuth =
    sessionStorage.getItem('ts_auth') === 'true' ||
    localStorage.getItem('ts_auth_remember') === 'true';

  if (!hasActiveAuth) {
    return { isValid: false, reason: 'Oturum açılmamış.' };
  }

  const currentUser = getCurrentUser();
  if (!currentUser) {
    return { isValid: false, reason: 'Oturum bilgisi bulunamadı.' };
  }

  if (!serverData) {
    return { isValid: true };
  }

  const serverUsers = serverData.users || [];
  const serverCompany = serverData.company;

  // Identify if logged in user is Master Admin
  const isMasterAdmin =
    currentUser.id === 'usr_admin' ||
    currentUser.username.toLowerCase().trim() === (serverCompany?.adminUsername || 'admin').toLowerCase().trim() ||
    currentUser.username.toLowerCase().trim() === 'admin';

  // 1. Check in server users list
  const matchedUser = serverUsers.find(
    u => u.id === currentUser.id || u.username.toLowerCase().trim() === currentUser.username.toLowerCase().trim()
  );

  if (!matchedUser && !isMasterAdmin) {
    return {
      isValid: false,
      reason: '⚠️ Kullanıcı hesabınız sistemden silindiği için bu cihazdaki oturumunuz sonlandırıldı.',
    };
  }

  if (matchedUser) {
    if (!isMasterAdmin && matchedUser.isActive === false) {
      return {
        isValid: false,
        reason: '⚠️ Kullanıcı hesabınız pasife alındığı için bu cihazdaki oturumunuz sonlandırıldı.',
      };
    }

  }

  // 2. For Master Admin, also check company credentials & passwordVersion
  if (isMasterAdmin && serverCompany) {
    const serverPasswordVersion = serverCompany.passwordVersion;
    if (serverPasswordVersion) {
      setCurrentAuthVersion(serverPasswordVersion);
    }
  }

  return { isValid: true };
}

export function getStoredDeviceTypes(): string[] {
  try {
    const raw = localStorage.getItem(DEVICE_TYPES_KEY);
    if (!raw) {
      return defaultDeviceTypes;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : defaultDeviceTypes;
  } catch (err) {
    console.error('Error loading device types from storage:', err);
    return defaultDeviceTypes;
  }
}

// Yazma işlemleri ile 3.5 sn'lik otomatik okuma aynı ana denk gelirse eski sunucu
// cevabının yeni veriyi ekranda geri çevirmesini engeller. Ticket dışında stok,
// şirket, cihaz listeleri ve sözleşmeli firmalar için de aynı yarış mümkündür.
type SyncEntityKey = 'tickets' | 'company' | 'deviceTypes' | 'deviceBrands' | 'inventory' | 'inventoryCategories' | 'stockMovements' | 'contractedCompanies' | 'users';
const pendingEntityWrites: Record<SyncEntityKey, number> = {
  tickets: 0, company: 0, deviceTypes: 0, deviceBrands: 0, inventory: 0,
  inventoryCategories: 0, stockMovements: 0, contractedCompanies: 0, users: 0,
};
const lastEntityWriteCompletedAt: Record<SyncEntityKey, number> = {
  tickets: 0, company: 0, deviceTypes: 0, deviceBrands: 0, inventory: 0,
  inventoryCategories: 0, stockMovements: 0, contractedCompanies: 0, users: 0,
};

function isEntityWriteHot(key: SyncEntityKey): boolean {
  return pendingEntityWrites[key] > 0 || Date.now() - lastEntityWriteCompletedAt[key] < 2000;
}

function syncTrackedEntity(key: SyncEntityKey, payload: Record<string, any>): Promise<void> {
  pendingEntityWrites[key]++;
  return syncEntityToServer(payload).finally(() => {
    pendingEntityWrites[key] = Math.max(0, pendingEntityWrites[key] - 1);
    lastEntityWriteCompletedAt[key] = Date.now();
  });
}

export async function syncEntityToServer(payload: Record<string, any>): Promise<void> {
  try {
    const databaseInstance = localStorage.getItem(DATABASE_INSTANCE_KEY) || '';
    // PHP/MySQL is the canonical storage on Hostinger. Do not trust a 200 HTML response from SPA rewrites.
    const res = await fetch('/api.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ ...payload, _databaseInstance: databaseInstance }),
    });
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) throw new Error('Sunucu JSON yerine farklı içerik döndürdü.');
    const result = await res.json();
    if (res.status === 409 && result?.error === 'DATABASE_CHANGED') {
      clearSensitiveLocalCache();
      if (result.databaseInstance) localStorage.setItem(DATABASE_INSTANCE_KEY, String(result.databaseInstance));
      window.dispatchEvent(new CustomEvent('tekservis-database-changed'));
      throw new Error('DATABASE_CHANGED');
    }
    if (!res.ok) throw new Error(`Senkronizasyon HTTP ${res.status}`);
    if (!result || (result.status !== 'success' && result.saved !== true)) {
      throw new Error(result?.message || 'Sunucu kaydı doğrulanamadı.');
    }
  } catch (err) {
    console.error('MySQL/PHP senkronizasyon hatası:', err);
    throw err;
  }
}

export function saveDeviceTypes(deviceTypes: string[]): void {
  try {
    localStorage.setItem(DEVICE_TYPES_KEY, JSON.stringify(deviceTypes));
    syncTrackedEntity('deviceTypes', { deviceTypes }).catch(() => {});
  } catch (err) {
    console.error('Error saving device types:', err);
  }
}

export function getStoredDeviceBrands(): string[] {
  try {
    const raw = localStorage.getItem(DEVICE_BRANDS_KEY);
    if (!raw) {
      localStorage.setItem(DEVICE_BRANDS_KEY, JSON.stringify(defaultDeviceBrands));
      return defaultDeviceBrands;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : defaultDeviceBrands;
  } catch (err) {
    console.error('Error loading device brands from storage:', err);
    return defaultDeviceBrands;
  }
}

export function saveDeviceBrands(deviceBrands: string[]): void {
  try {
    const brands = deviceBrands && deviceBrands.length > 0 ? deviceBrands : defaultDeviceBrands;
    localStorage.setItem(DEVICE_BRANDS_KEY, JSON.stringify(brands));
    syncTrackedEntity('deviceBrands', { deviceBrands: brands }).catch(() => {});
  } catch (err) {
    console.error('Error saving device brands:', err);
  }
}

export function addStoredDeviceBrand(newBrand: string): void {
  if (!newBrand || !newBrand.trim()) return;
  const brandName = newBrand.trim();
  const current = getStoredDeviceBrands();
  const exists = current.some(b => b.toLowerCase() === brandName.toLowerCase());
  if (!exists) {
    const updated = [...current, brandName].sort((a, b) => a.localeCompare(b, 'tr'));
    saveDeviceBrands(updated);
  }
}

export function cleanTechnicianNotes(notes?: string): string {
  if (!notes) return '';
  return notes
    .split('\n')
    .filter(line => {
      const trimmed = line.trim();
      if (/^\[\d{1,2}[./-]\d{1,2}[./-]\d{2,4}.*?\]\s*.*?(Tahsilat|Tahsilatı)/i.test(trimmed)) {
        return false;
      }
      return true;
    })
    .join('\n')
    .trim();
}

export function getStoredTickets(): ServiceTicket[] {
  try {
    const raw = localStorage.getItem(TICKETS_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(t => ({
      ...t,
      status: normalizeTicketStatus(t.status),
      technicianNotes: t.technicianNotes ? cleanTechnicianNotes(t.technicianNotes) : undefined,
    }));
  } catch (err) {
    console.error('Error loading tickets from storage:', err);
    return [];
  }
}

export function saveTickets(tickets: ServiceTicket[], replaceAll = true): void {
  try {
    const safeTickets = Array.isArray(tickets) ? tickets : [];
    if (replaceAll) {
      localStorage.setItem(TICKETS_KEY, JSON.stringify(safeTickets));
    } else {
      const current = getStoredTickets();
      const map = new Map(current.map(t => [t.id, t]));
      safeTickets.forEach(t => map.set(t.id, t));
      localStorage.setItem(TICKETS_KEY, JSON.stringify(Array.from(map.values())));
    }
    syncTrackedEntity('tickets', { tickets: safeTickets, replace_all_tickets: replaceAll }).catch(err => {
      console.error('Error saving tickets to MySQL:', err);
    });
  } catch (err) {
    console.error('Error saving tickets to storage:', err);
  }
}

export function getStoredCompanyInfo(): CompanyInfo {
  try {
    const raw = localStorage.getItem(COMPANY_KEY);
    if (!raw) {
      return defaultCompanyInfo;
    }
    const parsed = JSON.parse(raw);
    return parsed && parsed.name ? parsed : defaultCompanyInfo;
  } catch (err) {
    console.error('Error loading company info from storage:', err);
    return defaultCompanyInfo;
  }
}

export function saveCompanyInfo(company: CompanyInfo): void {
  try {
    const { adminPassword, securityAnswer, ...safeCompany } = company;
    localStorage.setItem(COMPANY_KEY, JSON.stringify(safeCompany));

    if (company.passwordVersion) {
      setCurrentAuthVersion(company.passwordVersion);
    }

    syncTrackedEntity('company', { company }).catch(() => {});
  } catch (err) {
    console.error('Error saving company info:', err);
  }
}

export function getStoredInventory(): InventoryItem[] {
  try {
    const raw = localStorage.getItem(INVENTORY_KEY);
    if (!raw) {
      localStorage.setItem(INVENTORY_KEY, JSON.stringify([]));
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error('Error loading inventory from storage:', err);
    return [];
  }
}

export function saveInventory(inventory: InventoryItem[]): void {
  try {
    localStorage.setItem(INVENTORY_KEY, JSON.stringify(inventory));
    syncTrackedEntity('inventory', { inventory }).catch(() => {});
  } catch (err) {
    console.error('Error saving inventory:', err);
  }
}

export function getStoredInventoryCategories(): string[] {
  try {
    const raw = localStorage.getItem(INVENTORY_CATEGORIES_KEY);
    if (!raw) {
      localStorage.setItem(INVENTORY_CATEGORIES_KEY, JSON.stringify(defaultInventoryCategories));
      return defaultInventoryCategories;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed;
    }
    return defaultInventoryCategories;
  } catch (err) {
    console.error('Error loading inventory categories from storage:', err);
    return defaultInventoryCategories;
  }
}

export function saveInventoryCategories(categories: string[]): void {
  try {
    const finalCategories = categories.length > 0 ? categories : defaultInventoryCategories;
    localStorage.setItem(INVENTORY_CATEGORIES_KEY, JSON.stringify(finalCategories));
    syncTrackedEntity('inventoryCategories', { inventoryCategories: finalCategories }).catch(() => {});
  } catch (err) {
    console.error('Error saving inventory categories:', err);
  }
}

export function getStoredStockMovements(): StockMovement[] {
  try {
    const raw = localStorage.getItem(STOCK_MOVEMENTS_KEY);
    if (!raw) {
      localStorage.setItem(STOCK_MOVEMENTS_KEY, JSON.stringify([]));
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error('Error loading stock movements from storage:', err);
    return [];
  }
}

export function saveStockMovements(movements: StockMovement[]): void {
  try {
    localStorage.setItem(STOCK_MOVEMENTS_KEY, JSON.stringify(movements));
    syncTrackedEntity('stockMovements', { stockMovements: movements }).catch(() => {});
  } catch (err) {
    console.error('Error saving stock movements:', err);
  }
}

export function generateNextInventoryId(items: InventoryItem[]): string {
  const prefix = 'STK-';
  let maxNum = 1000;
  items.forEach(it => {
    if (it.id.startsWith(prefix)) {
      const numPart = parseInt(it.id.replace(prefix, ''), 10);
      if (!isNaN(numPart) && numPart > maxNum) {
        maxNum = numPart;
      }
    }
  });
  return `${prefix}${maxNum + 1}`;
}

export function resetToDefaults(): void {
  localStorage.setItem(TICKETS_KEY, JSON.stringify(initialTickets));
  localStorage.setItem(COMPANY_KEY, JSON.stringify(defaultCompanyInfo));
  localStorage.setItem(INVENTORY_KEY, JSON.stringify(initialInventoryItems));
  localStorage.setItem(INVENTORY_CATEGORIES_KEY, JSON.stringify(defaultInventoryCategories));
  localStorage.setItem(STOCK_MOVEMENTS_KEY, JSON.stringify(initialStockMovements));

  fetch('/api.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tickets: initialTickets,
      company: defaultCompanyInfo,
      inventory: initialInventoryItems,
      inventoryCategories: defaultInventoryCategories,
      stockMovements: initialStockMovements,
      replace_all_tickets: true,
    }),
  }).catch(() => {});

  fetch('/api/reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tickets: initialTickets,
      company: defaultCompanyInfo,
      inventory: initialInventoryItems,
      inventoryCategories: defaultInventoryCategories,
      stockMovements: initialStockMovements,
    }),
  }).catch(() => {});
}

function mergeTickets(local: ServiceTicket[], server: ServiceTicket[]): ServiceTicket[] {
  const map = new Map<string, ServiceTicket>();

  server.forEach(t => map.set(t.id, t));

  local.forEach(lt => {
    const st = map.get(lt.id);
    if (!st) {
      map.set(lt.id, lt);
    } else {
      const isSameRecord =
        st.createdAt === lt.createdAt &&
        st.customerName === lt.customerName &&
        st.customerPhone === lt.customerPhone;

      if (isSameRecord) {
        const localTime = new Date(lt.updatedAt || lt.createdAt || 0).getTime();
        const serverTime = new Date(st.updatedAt || st.createdAt || 0).getTime();
        if (localTime > serverTime) {
          map.set(lt.id, lt);
        }
      } else {
        // Çakışma önleyici: 2 farklı cihaz aynı anda/çevrimdışı aynı form numarasını açtıysa
        // İkisi de korunur, yeni olan sonraki müsait numarayı (örn. 1002) alarak üst üste binmesi engellenir
        const currentTickets = Array.from(map.values());
        const nextId = generateNextTicketId(currentTickets);
        const fixedTicket = { ...lt, id: nextId };
        map.set(nextId, fixedTicket);
      }
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
  });
}

export async function fetchServerData(): Promise<{
  tickets: ServiceTicket[];
  company: CompanyInfo;
  deviceTypes: string[];
  deviceBrands: string[];
  inventory: InventoryItem[];
  inventoryCategories: string[];
  stockMovements: StockMovement[];
  users: AppUser[];
  contractedCompanies?: ContractedCompany[];
  exchangeRate?: ExchangeRateInfo;
  databaseInstance?: string;
} | null> {
  // SQL-ONLY: MySQL/PHP is the only persistent source of truth; no offline data fallback.
  try {
    const phpRes = await fetch(`/api.php?action=get&_t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Pragma': 'no-cache',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
      },
    });

    const contentType = phpRes.headers.get('content-type') || '';
    if (phpRes.status === 401) {
      // Oturum durumunu React katmanı yönetir. Burada storage'ı anında silmek,
      // login ile eşzamanlı çalışan isteklerde yeni oturumu yanlışlıkla düşürebilir.
      throw new Error('SESSION_UNAUTHORIZED');
    }
    let data: any = null;
    if (contentType.includes('application/json')) {
      data = await phpRes.json().catch(() => null);
    }
    if (phpRes.status === 503 && data?.error === 'MYSQL_REQUIRED') {
      throw new Error('MYSQL_REQUIRED');
    }
    if (!phpRes.ok || !contentType.includes('application/json')) {
      throw new Error(`PHP veri API hatası: HTTP ${phpRes.status}`);
    }
    if (!data || typeof data !== 'object' || data.error || data.status === 'error') {
      throw new Error(data?.message || 'Sunucudan geçersiz veri döndü.');
    }

    const serverDatabaseInstance = String(data?._meta?.databaseInstance || '');
    const previousDatabaseInstance = localStorage.getItem(DATABASE_INSTANCE_KEY) || '';
    if (serverDatabaseInstance && previousDatabaseInstance && serverDatabaseInstance !== previousDatabaseInstance) {
      // Aktif MySQL değişti: eski veritabanına ait hiçbir tarayıcı verisini yeni
      // veritabanıyla birleştirme. Oturum korunur, uygulama verisi sıfırlanır.
      clearSensitiveLocalCache();
    }
    if (serverDatabaseInstance) {
      localStorage.setItem(DATABASE_INSTANCE_KEY, serverDatabaseInstance);
    }

    let serverTickets: ServiceTicket[] = (Array.isArray(data.tickets) ? data.tickets : []).map((t: ServiceTicket) => ({
      ...t,
      status: normalizeTicketStatus(t.status),
      technicianNotes: t.technicianNotes ? cleanTechnicianNotes(t.technicianNotes) : undefined,
    }));

    // Bir durum/aşama POST'u sürerken (ve hemen sonrasındaki kısa yarış penceresinde)
    // daha önce başlamış GET eski kaydı döndürebilir. Bu durumda yalnızca gerçekten
    // daha yeni updatedAt taşıyan yerel ticket korunur; sonraki poll MySQL ile eşitlenir.
    if (isEntityWriteHot('tickets')) {
      const localTickets = getStoredTickets();
      const localMap = new Map(localTickets.map(t => [t.id, t]));
      serverTickets = serverTickets.map(serverTicket => {
        const localTicket = localMap.get(serverTicket.id);
        if (!localTicket) return serverTicket;
        const serverTime = Date.parse(serverTicket.updatedAt || serverTicket.createdAt || '') || 0;
        const localTime = Date.parse(localTicket.updatedAt || localTicket.createdAt || '') || 0;
        return localTime > serverTime ? localTicket : serverTicket;
      });
      const serverIds = new Set(serverTickets.map(t => t.id));
      for (const localTicket of localTickets) {
        if (!serverIds.has(localTicket.id)) serverTickets.push(localTicket);
      }
    }
    const serverCompany: CompanyInfo = isEntityWriteHot('company')
      ? getStoredCompanyInfo()
      : (data.company && data.company.name ? data.company : defaultCompanyInfo);
    const serverDeviceTypes: string[] = isEntityWriteHot('deviceTypes')
      ? getStoredDeviceTypes()
      : (Array.isArray(data.deviceTypes) && data.deviceTypes.length > 0 ? data.deviceTypes : defaultDeviceTypes);
    const serverDeviceBrands: string[] = isEntityWriteHot('deviceBrands')
      ? getStoredDeviceBrands()
      : (Array.isArray(data.deviceBrands) && data.deviceBrands.length > 0 ? data.deviceBrands : defaultDeviceBrands);
    const serverInventory: InventoryItem[] = isEntityWriteHot('inventory')
      ? getStoredInventory()
      : (Array.isArray(data.inventory) ? data.inventory : []);
    const serverCategories: string[] = isEntityWriteHot('inventoryCategories')
      ? getStoredInventoryCategories()
      : (Array.isArray(data.inventoryCategories) && data.inventoryCategories.length > 0 ? data.inventoryCategories : defaultInventoryCategories);
    const serverMovements: StockMovement[] = isEntityWriteHot('stockMovements')
      ? getStoredStockMovements()
      : (Array.isArray(data.stockMovements) ? data.stockMovements : []);
    const serverUsers: AppUser[] = isEntityWriteHot('users')
      ? getStoredUsers()
      : (Array.isArray(data.users) ? data.users : []);
    const serverContracted: ContractedCompany[] = isEntityWriteHot('contractedCompanies')
      ? getStoredContractedCompanies()
      : (Array.isArray(data.contractedCompanies) ? data.contractedCompanies : []);

    // Refresh offline cache from server; never merge stale browser data back into a successful server read.
    localStorage.setItem(TICKETS_KEY, JSON.stringify(serverTickets));
    localStorage.setItem(COMPANY_KEY, JSON.stringify(serverCompany));
    localStorage.setItem(DEVICE_TYPES_KEY, JSON.stringify(serverDeviceTypes));
    localStorage.setItem(DEVICE_BRANDS_KEY, JSON.stringify(serverDeviceBrands));
    localStorage.setItem(INVENTORY_KEY, JSON.stringify(serverInventory));
    localStorage.setItem(INVENTORY_CATEGORIES_KEY, JSON.stringify(serverCategories));
    localStorage.setItem(STOCK_MOVEMENTS_KEY, JSON.stringify(serverMovements));
    localStorage.setItem(USERS_KEY, JSON.stringify(serverUsers));
    localStorage.setItem(CONTRACTED_COMPANIES_KEY, JSON.stringify(serverContracted));
    if (data.labelConfig && typeof data.labelConfig === 'object') {
      localStorage.setItem('service_custom_label_config_v2', JSON.stringify(data.labelConfig));
    }
    if (data.smsSettings && typeof data.smsSettings === 'object') {
      localStorage.setItem('tekservis_sms_settings', JSON.stringify(data.smsSettings));
    }
    if (Array.isArray(data.smsLogs)) {
      localStorage.setItem('tekservis_sms_logs', JSON.stringify(data.smsLogs));
    }
    if (data.exchangeRate && typeof data.exchangeRate === 'object' && typeof data.exchangeRate.rate === 'number') {
      localStorage.setItem(EXCHANGE_RATE_KEY, JSON.stringify(data.exchangeRate));
    }

    return {
      tickets: serverTickets,
      company: serverCompany,
      deviceTypes: serverDeviceTypes,
      deviceBrands: serverDeviceBrands,
      inventory: serverInventory,
      inventoryCategories: serverCategories,
      stockMovements: serverMovements,
      users: serverUsers,
      contractedCompanies: serverContracted,
      exchangeRate: data.exchangeRate || getStoredExchangeRate(),
      databaseInstance: serverDatabaseInstance,
    };
  } catch (err) {
    console.error('MySQL verisi alınamadı. SQL-only modda çevrimdışı veri fallback kullanılmaz:', err);
    if (err instanceof Error && (err.message === 'SESSION_UNAUTHORIZED' || err.message === 'MYSQL_REQUIRED')) {
      throw err;
    }
    return null;
  }
}

export async function checkServerConnection(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3500);

    const phpRes = await fetch(`/api.php?action=get&_t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Pragma': 'no-cache', 'Cache-Control': 'no-cache' },
      signal: controller.signal,
    }).catch(() => null);

    clearTimeout(timeoutId);

    if (phpRes && phpRes.ok) {
      return true;
    }
  } catch {
    // Continue
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3500);

    const res = await fetch(`/api/health?_t=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    }).catch(() => null);

    clearTimeout(timeoutId);

    if (res && res.ok) {
      return true;
    }
  } catch {
    // Fail
  }

  return false;
}

export function getCompanyInitials(name?: string): string {
  if (!name || typeof name !== 'string' || !name.trim()) return 'TS';

  const cleanName = name.trim();
  const words = cleanName.split(/[\s\-_\/&+,.]+/).filter(w => w.length > 0);
  if (words.length === 0) return 'TS';

  let initials = words
    .map(w => {
      const match = w.match(/[a-zA-ZğüşıöçĞÜŞİÖÇ0-9]/);
      return match ? match[0].toLocaleUpperCase('tr-TR') : '';
    })
    .join('');

  if (initials.length === 1 && words[0].length >= 2) {
    const letters = words[0].replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ0-9]/g, '');
    initials = letters.slice(0, 2).toLocaleUpperCase('tr-TR');
  }

  return initials || 'TS';
}

export function generateNextTicketId(tickets: ServiceTicket[], companyName?: string): string {
  const company = companyName || getStoredCompanyInfo()?.name || '';
  const initials = getCompanyInitials(company);
  const prefix = `${initials}-`;
  
  let maxNum = 1000;
  tickets.forEach(t => {
    if (!t || !t.id) return;
    if (t.id.startsWith(prefix)) {
      const numPart = parseInt(t.id.slice(prefix.length), 10);
      if (!isNaN(numPart) && numPart > maxNum) {
        maxNum = numPart;
      }
    } else {
      const lastDash = t.id.lastIndexOf('-');
      if (lastDash !== -1) {
        const numPart = parseInt(t.id.substring(lastDash + 1), 10);
        if (!isNaN(numPart) && numPart >= 1000 && numPart > maxNum) {
          maxNum = numPart;
        }
      }
    }
  });
  
  return `${prefix}${maxNum + 1}`;
}

export function formatTL(amount: number): string {
  const num = typeof amount === 'number' && !isNaN(amount) && isFinite(amount) ? amount : Number(amount) || 0;
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(isNaN(num) || !isFinite(num) ? 0 : num);
}

export function formatUSD(amount: number): string {
  const num = typeof amount === 'number' && !isNaN(amount) && isFinite(amount) ? amount : Number(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(isNaN(num) || !isFinite(num) ? 0 : num);
}

/**
 * Müşteri telefon numaralarını 0555-555-55-55 formatında görüntüler.
 * Kullanıcı giriş yaparken 05555555555 şeklinde birleşik girer,
 * bilgi alanlarında ve çıktılarda 0555-555-55-55 olarak ayrıştırılmış gösterilir.
 */
export function formatPhoneDisplay(phone?: string | null): string {
  if (!phone) return '';
  const str = String(phone).trim();
  const digits = str.replace(/\D/g, '');
  if (!digits) return str;

  let d = digits;
  // +90 veya 90 ülke kodunu ayıkla
  if (d.startsWith('90') && d.length === 12) {
    d = '0' + d.slice(2);
  } else if (!d.startsWith('0') && d.length === 10) {
    d = '0' + d;
  }

  // 11 haneli standart GSM (05XX-XXX-XX-XX)
  if (d.length === 11) {
    return `${d.slice(0, 4)}-${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
  } else if (d.length === 10) {
    return `0${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
  } else if (d.length === 7) {
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5, 7)}`;
  } else if (d.length > 7 && d.length < 11) {
    return `${d.slice(0, 4)}-${d.slice(4, 7)}-${d.slice(7)}`;
  } else if (d.length > 11) {
    return `${d.slice(0, 4)}-${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}-${d.slice(11)}`;
  }
  return d;
}

/**
 * Telefon giriş alanlarında sadece rakam kabul eder ve birleşik 11 hane ile sınırlar (05555555555)
 */
export function sanitizePhoneInput(val: string): string {
  if (!val) return '';
  return val.replace(/\D/g, '').slice(0, 11);
}

/**
 * Telefon numarasının eksiksiz girilip girilmediğini doğrular (10 veya 11 hane)
 */
export function validatePhoneDigits(val: string): boolean {
  if (!val) return false;
  const digits = val.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 11;
}

export function convertUSDToTL(amountUSD: number, rate?: number): number {
  if (!amountUSD || isNaN(amountUSD)) return 0;
  const currentRate = rate && rate > 0 ? rate : getStoredExchangeRate().rate;
  return Math.round(amountUSD * currentRate * 100) / 100;
}

export function convertTLToUSD(amountTL: number, rate?: number): number {
  if (!amountTL || isNaN(amountTL)) return 0;
  const currentRate = rate && rate > 0 ? rate : getStoredExchangeRate().rate;
  if (currentRate <= 0) return 0;
  return Math.round((amountTL / currentRate) * 100) / 100;
}

export function getPartPriceTL(part: { unitPrice: number; unitPriceUSD?: number; currency?: CurrencyCode; exchangeRate?: number; saleExchangeRate?: number }, activeRate?: number): number {
  if (part.currency === 'USD') {
    if (part.unitPriceUSD !== undefined && part.unitPriceUSD > 0) {
      const rate = part.saleExchangeRate && part.saleExchangeRate > 0
        ? part.saleExchangeRate
        : (part.exchangeRate && part.exchangeRate > 0 ? part.exchangeRate : (activeRate || getStoredExchangeRate().rate));
      return Math.round(part.unitPriceUSD * rate * 100) / 100;
    }
  }
  return part.unitPrice || 0;
}

export function getPartCostTL(part: { costPrice?: number; costPriceUSD?: number; currency?: CurrencyCode; exchangeRate?: number; costExchangeRate?: number; unitPrice?: number }, activeRate?: number): number {
  if (part.currency === 'USD') {
    if (part.costPriceUSD !== undefined && part.costPriceUSD > 0) {
      const rate = part.costExchangeRate && part.costExchangeRate > 0
        ? part.costExchangeRate
        : (part.exchangeRate && part.exchangeRate > 0 ? part.exchangeRate : (activeRate || getStoredExchangeRate().rate));
      return Math.round(part.costPriceUSD * rate * 100) / 100;
    }
  }
  return part.costPrice !== undefined ? part.costPrice : (part.unitPrice || 0);
}

export function getItemPriceTL(item: { salePrice: number; salePriceUSD?: number; currency?: CurrencyCode; exchangeRate?: number; saleExchangeRate?: number }, activeRate?: number): number {
  if (item.currency === 'USD') {
    if (item.salePriceUSD !== undefined && item.salePriceUSD > 0) {
      const rate = item.saleExchangeRate && item.saleExchangeRate > 0
        ? item.saleExchangeRate
        : (item.exchangeRate && item.exchangeRate > 0 ? item.exchangeRate : (activeRate || getStoredExchangeRate().rate));
      return Math.round(item.salePriceUSD * rate * 100) / 100;
    }
  }
  return item.salePrice || 0;
}

export function getItemCostTL(item: { costPrice: number; costPriceUSD?: number; currency?: CurrencyCode; exchangeRate?: number; costExchangeRate?: number; lots?: InventoryLot[] }, activeRate?: number): number {
  const currentRate = activeRate || getStoredExchangeRate().rate || 47.96;
  // Eğer partiler (lots) tanımlıysa ve kalan parti varsa en düşük maliyetli aktif partinin maliyetini döndür
  if (item.lots && item.lots.length > 0) {
    const activeLots = item.lots.filter(l => l.remainingQuantity > 0);
    if (activeLots.length > 0) {
      const sorted = [...activeLots].sort((a, b) => getLotCostTL(a, currentRate) - getLotCostTL(b, currentRate));
      return getLotCostTL(sorted[0], currentRate);
    }
  }

  if (item.currency === 'USD') {
    if (item.costPriceUSD !== undefined && item.costPriceUSD > 0) {
      const rate = item.costExchangeRate && item.costExchangeRate > 0
        ? item.costExchangeRate
        : (item.exchangeRate && item.exchangeRate > 0 ? item.exchangeRate : currentRate);
      return Math.round(item.costPriceUSD * rate * 100) / 100;
    }
  }
  return item.costPrice || 0;
}

// Bir partinin TL cinsinden birim maliyeti (o günkü alış kuru veya sabit kur ile)
export function getLotCostTL(lot: InventoryLot, activeRate?: number): number {
  if (lot.currency === 'USD' && lot.costPriceUSD !== undefined && lot.costPriceUSD > 0) {
    const rate = lot.exchangeRate && lot.exchangeRate > 0
      ? lot.exchangeRate
      : (activeRate || getStoredExchangeRate().rate || 47.96);
    return Math.round(lot.costPriceUSD * rate * 100) / 100;
  }
  return lot.costPrice || 0;
}

// Bir ürünün mevcut aktif (stokta kalan) partilerini getirme
export function getItemActiveLots(item: InventoryItem, activeRate?: number): InventoryLot[] {
  const currentRate = activeRate || getStoredExchangeRate().rate || 47.96;
  if (!item.lots || item.lots.length === 0) {
    if (item.quantity > 0) {
      return [{
        id: `LOT-${item.id}-init`,
        itemId: item.id,
        quantity: item.quantity,
        remainingQuantity: item.quantity,
        costPrice: item.costPrice || 0,
        costPriceUSD: item.costPriceUSD,
        exchangeRate: item.costExchangeRate || item.exchangeRate || currentRate,
        currency: item.currency || 'TRY',
        purchaseDate: item.createdAt || new Date().toISOString(),
        supplier: item.supplier,
        note: 'Mevcut Stok Partisi'
      }];
    }
    return [];
  }
  return item.lots.filter(l => l.remainingQuantity > 0);
}

// Partileri EN DÜŞÜK TL MALİYETLİ OLAN İLK SIRAYA GELECEK ŞEKİLDE sıralama
export function getSortedLotsByLowestCost(item: InventoryItem, activeRate?: number): InventoryLot[] {
  const lots = getItemActiveLots(item, activeRate);
  return [...lots].sort((a, b) => {
    const costA = getLotCostTL(a, activeRate);
    const costB = getLotCostTL(b, activeRate);
    if (Math.abs(costA - costB) > 0.001) {
      return costA - costB; // En ucuz olan parti ilk sırada tükenir
    }
    // Maliyet aynıysa alım tarihi eski olan önce (FIFO)
    return new Date(a.purchaseDate).getTime() - new Date(b.purchaseDate).getTime();
  });
}

// Bir ürünün tüm aktif partilerine göre Ağırlıklı Ortalama Birim Maliyetini hesaplar
export function calculateItemWeightedAvgCost(item: InventoryItem, activeRate?: number): number {
  const currentRate = activeRate || getStoredExchangeRate().rate || 47.96;
  const activeLots = getItemActiveLots(item, currentRate);
  if (!activeLots || activeLots.length === 0) {
    return getItemCostTL(item, currentRate);
  }

  let totalValue = 0;
  let totalQty = 0;

  activeLots.forEach(lot => {
    const qty = lot.remainingQuantity || 0;
    if (qty > 0) {
      const cost = getLotCostTL(lot, currentRate);
      totalValue += qty * cost;
      totalQty += qty;
    }
  });

  if (totalQty <= 0) {
    return getItemCostTL(item, currentRate);
  }

  return Math.round((totalValue / totalQty) * 100) / 100;
}

// Bir ürünün depodaki toplam stok maliyet değerini (TL) hesaplar
export function calculateItemTotalInventoryValue(item: InventoryItem, activeRate?: number): number {
  const currentRate = activeRate || getStoredExchangeRate().rate || 47.96;
  const activeLots = getItemActiveLots(item, currentRate);
  if (!activeLots || activeLots.length === 0) {
    return (item.quantity || 0) * getItemCostTL(item, currentRate);
  }

  let totalValue = 0;
  activeLots.forEach(lot => {
    const qty = lot.remainingQuantity || 0;
    if (qty > 0) {
      const cost = getLotCostTL(lot, currentRate);
      totalValue += qty * cost;
    }
  });

  return Math.round(totalValue * 100) / 100;
}

// Yeni parti alımı ekleme (Mevcut partileri ve eski maliyeti bozmadan yeni parti kaydeder)
export function addInventoryLotPurchase(
  item: InventoryItem,
  purchaseQty: number,
  unitCostTL: number,
  currency: CurrencyCode = 'TRY',
  unitCostUSD?: number,
  exchangeRate?: number,
  supplier?: string,
  note?: string
): InventoryItem {
  const currentRate = exchangeRate || getStoredExchangeRate().rate || 47.96;
  const nowISO = new Date().toISOString();

  // Mevcut partileri topla veya eski stoğu ilk parti olarak başlat
  let currentLots: InventoryLot[] = [];
  if (item.lots && item.lots.length > 0) {
    currentLots = [...item.lots];
  } else if ((item.quantity || 0) > 0) {
    currentLots = [{
      id: `LOT-${item.id}-init`,
      itemId: item.id,
      quantity: item.quantity,
      remainingQuantity: item.quantity,
      costPrice: item.costPrice || 0,
      costPriceUSD: item.costPriceUSD,
      exchangeRate: item.costExchangeRate || item.exchangeRate || currentRate,
      currency: item.currency || 'TRY',
      purchaseDate: item.createdAt || nowISO,
      supplier: item.supplier,
      note: 'Önceki Mevcut Stok Partisi'
    }];
  }

  // Yeni alım partisini oluştur
  const newLot: InventoryLot = {
    id: `LOT-${item.id}-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    itemId: item.id,
    quantity: purchaseQty,
    remainingQuantity: purchaseQty,
    costPrice: unitCostTL,
    costPriceUSD: unitCostUSD,
    exchangeRate: currency === 'USD' ? currentRate : undefined,
    currency: currency,
    purchaseDate: nowISO,
    supplier: supplier || item.supplier,
    note: note || 'Yeni Mal Alımı / Parti Girişi'
  };

  const updatedLots = [...currentLots, newLot];
  const newTotalQty = (item.quantity || 0) + purchaseQty;

  // Sıradaki en düşük çıkış maliyetini belirle
  const activeLots = updatedLots.filter(l => l.remainingQuantity > 0);
  let nextCostTL = unitCostTL;
  let nextCostUSD = unitCostUSD;
  let nextExchangeRate = currency === 'USD' ? currentRate : undefined;

  if (activeLots.length > 0) {
    const sorted = [...activeLots].sort((a, b) => getLotCostTL(a, currentRate) - getLotCostTL(b, currentRate));
    const best = sorted[0];
    nextCostTL = getLotCostTL(best, currentRate);
    nextCostUSD = best.costPriceUSD;
    nextExchangeRate = best.exchangeRate;
  }

  return {
    ...item,
    quantity: newTotalQty,
    costPrice: nextCostTL,
    costPriceUSD: nextCostUSD,
    costExchangeRate: nextExchangeRate,
    lots: updatedLots,
    updatedAt: nowISO
  };
}

// İptal edilen / silinen servisten stok iadesi yapıldığında partilere geri yükleme
export function restoreItemStockLots(
  item: InventoryItem,
  returnQty: number,
  activeRate?: number
): InventoryItem {
  const currentRate = activeRate || getStoredExchangeRate().rate || 47.96;
  const nowISO = new Date().toISOString();

  let updatedLots: InventoryLot[] = item.lots ? [...item.lots] : [];
  if (updatedLots.length === 0) {
    return {
      ...item,
      quantity: (item.quantity || 0) + returnQty,
      updatedAt: nowISO
    };
  }

  // Son partiden başlayarak kapasiteye göre doldur veya son partiye ekle
  let remainingReturn = returnQty;
  for (let i = updatedLots.length - 1; i >= 0; i--) {
    if (remainingReturn <= 0) break;
    const lot = updatedLots[i];
    const maxCapacity = lot.quantity || 0;
    const currentRemaining = lot.remainingQuantity || 0;
    const deficit = Math.max(0, maxCapacity - currentRemaining);

    if (deficit > 0) {
      const putBack = Math.min(deficit, remainingReturn);
      lot.remainingQuantity = currentRemaining + putBack;
      remainingReturn -= putBack;
    }
  }

  // Eğer hala artan iade varsa en son partiye ekle
  if (remainingReturn > 0 && updatedLots.length > 0) {
    const lastLot = updatedLots[updatedLots.length - 1];
    lastLot.remainingQuantity = (lastLot.remainingQuantity || 0) + remainingReturn;
    lastLot.quantity = Math.max(lastLot.quantity, lastLot.remainingQuantity);
  }

  const newTotalQty = (item.quantity || 0) + returnQty;

  const activeLots = updatedLots.filter(l => l.remainingQuantity > 0);
  let nextCostTL = item.costPrice;
  let nextCostUSD = item.costPriceUSD;
  let nextExchangeRate = item.costExchangeRate;

  if (activeLots.length > 0) {
    const sorted = [...activeLots].sort((a, b) => getLotCostTL(a, currentRate) - getLotCostTL(b, currentRate));
    const best = sorted[0];
    nextCostTL = getLotCostTL(best, currentRate);
    nextCostUSD = best.costPriceUSD;
    nextExchangeRate = best.exchangeRate;
  }

  return {
    ...item,
    quantity: newTotalQty,
    costPrice: nextCostTL,
    costPriceUSD: nextCostUSD,
    costExchangeRate: nextExchangeRate,
    lots: updatedLots,
    updatedAt: nowISO
  };
}

// Dövizli/TL parçalarda en düşük maliyetli partiden başlayarak stok düşümü yapma
export function consumeItemStockLowestCostFirst(
  item: InventoryItem,
  quantityToConsume: number,
  activeRate?: number
): {
  updatedItem: InventoryItem;
  consumedLots: Array<{
    lotId: string;
    quantity: number;
    costPriceTL: number;
    costPriceUSD?: number;
    exchangeRate?: number;
    currency?: CurrencyCode;
  }>;
  primaryCostTL: number;
  primaryCostUSD?: number;
  primaryExchangeRate?: number;
} {
  const currentRate = activeRate || getStoredExchangeRate().rate || 47.96;
  let remainingNeed = Math.max(0, quantityToConsume);
  const activeLots = getItemActiveLots(item, currentRate);
  const sortedLots = getSortedLotsByLowestCost(item, currentRate);

  const consumedLots: Array<{
    lotId: string;
    quantity: number;
    costPriceTL: number;
    costPriceUSD?: number;
    exchangeRate?: number;
    currency?: CurrencyCode;
  }> = [];

  // Mevcut tüm partileri haritada tut (stokta 0 kalmış olanlar da dahil geçmiş korunur)
  const lotMap = new Map<string, InventoryLot>();
  if (item.lots && item.lots.length > 0) {
    item.lots.forEach(l => lotMap.set(l.id, { ...l }));
  } else if (activeLots.length > 0) {
    activeLots.forEach(l => lotMap.set(l.id, { ...l }));
  }

  for (const lot of sortedLots) {
    if (remainingNeed <= 0) break;
    const available = lot.remainingQuantity || 0;
    if (available > 0) {
      const take = Math.min(available, remainingNeed);
      const costTL = getLotCostTL(lot, currentRate);
      consumedLots.push({
        lotId: lot.id,
        quantity: take,
        costPriceTL: costTL,
        costPriceUSD: lot.costPriceUSD,
        exchangeRate: lot.exchangeRate,
        currency: lot.currency
      });
      remainingNeed -= take;

      const target = lotMap.get(lot.id) || { ...lot };
      target.remainingQuantity = Math.max(0, target.remainingQuantity - take);
      lotMap.set(lot.id, target);
    }
  }

  const updatedLots = Array.from(lotMap.values());
  const newTotalQty = Math.max(0, (item.quantity || 0) - quantityToConsume);

  // İlk / en ucuz partiden alınan maliyet
  const firstConsumed = consumedLots[0];
  const primaryCostTL = firstConsumed ? firstConsumed.costPriceTL : (item.costPrice || 0);
  const primaryCostUSD = firstConsumed?.costPriceUSD ?? item.costPriceUSD;
  const primaryExchangeRate = firstConsumed?.exchangeRate ?? (item.costExchangeRate || item.exchangeRate || currentRate);

  // Sıradaki en düşük parti maliyetini stok kartı birim maliyetine yansıt
  const nextActive = updatedLots.filter(l => l.remainingQuantity > 0);
  let nextCostTL = item.costPrice;
  let nextCostUSD = item.costPriceUSD;
  let nextExchangeRate = item.costExchangeRate;

  if (nextActive.length > 0) {
    const nextSorted = [...nextActive].sort((a, b) => getLotCostTL(a, currentRate) - getLotCostTL(b, currentRate));
    const best = nextSorted[0];
    nextCostTL = getLotCostTL(best, currentRate);
    nextCostUSD = best.costPriceUSD;
    nextExchangeRate = best.exchangeRate;
  }

  const updatedItem: InventoryItem = {
    ...item,
    quantity: newTotalQty,
    costPrice: nextCostTL,
    costPriceUSD: nextCostUSD,
    costExchangeRate: nextExchangeRate,
    lots: updatedLots,
    updatedAt: new Date().toISOString()
  };

  return {
    updatedItem,
    consumedLots,
    primaryCostTL,
    primaryCostUSD,
    primaryExchangeRate
  };
}

export function formatDateTR(isoString?: string): string {
  if (!isoString) return '-';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return '-';
  }
}

export function formatDateShortTR(isoString?: string): string {
  if (!isoString) return '-';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch {
    return '-';
  }
}

export const DEFAULT_STATUS_COLOR: { bg: string; text: string; border: string } = {
  bg: 'bg-blue-50',
  text: 'text-blue-700',
  border: 'border-blue-200',
};

export function normalizeTicketStatus(status: any): TicketStatus {
  if (!status) return 'Kaydedildi';
  const s = String(status).trim();
  const rawStatusMap: Record<string, TicketStatus> = {
    Kaydedildi: 'Kaydedildi',
    Incelemede: 'Incelemede',
    TestEdiliyor: 'TestEdiliyor',
    'Test Ediliyor': 'TestEdiliyor',
    Sivida: 'Sivida',
    Sıvıda: 'Sivida',
    ParcaBekliyor: 'ParcaBekliyor',
    AltServiste: 'AltServiste',
    MusteriOnayi: 'MusteriOnayi',
    Onaylandi: 'Onaylandi',
    Onaylanmadi: 'Onaylanmadi',
    Tamirde: 'Tamirde',
    Hazir: 'Hazir',
    TeslimEdildi: 'TeslimEdildi',
    IadeEdilecek: 'IadeEdilecek',
    IadeEdildi: 'IadeEdildi',
    Iptal: 'Iptal',
  };
  if (rawStatusMap[s]) {
    return rawStatusMap[s];
  }
  const lower = s.toLowerCase().replace(/[\s_\-]+/g, '');
  switch (lower) {
    case 'kaydedildi':
    case 'kayit':
    case 'kayitalindi':
    case 'pending':
    case 'intake':
    case 'new':
    case 'open':
      return 'Kaydedildi';
    case 'incelemede':
    case 'arizatespiti':
    case 'tespit':
    case 'diagnosing':
    case 'investigating':
      return 'Incelemede';
    case 'testediliyor':
      return 'TestEdiliyor';
    case 'sivida':
    case 'sıvıda':
      return 'Sivida';
    case 'parcabekliyor':
    case 'waitingparts':
    case 'parts':
      return 'ParcaBekliyor';
    case 'altserviste':
    case 'subservice':
    case 'external':
      return 'AltServiste';
    case 'musterionayi':
    case 'onaybekliyor':
    case 'waitingapproval':
    case 'approvalwaiting':
      return 'MusteriOnayi';
    case 'onaylandi':
    case 'approved':
    case 'priceapproved':
      return 'Onaylandi';
    case 'onaylanmadi':
    case 'disapproved':
    case 'rejected':
      return 'Onaylanmadi';
    case 'tamirde':
    case 'onarimda':
    case 'inrepair':
    case 'inprogress':
    case 'repairing':
      return 'Tamirde';
    case 'hazir':
    case 'teslimatahazi':
    case 'teslimathazir':
    case 'ready':
    case 'completed':
    case 'done':
      return 'Hazir';
    case 'teslimedildi':
    case 'teslim':
    case 'delivered':
    case 'closed':
      return 'TeslimEdildi';
    case 'iadeedilecek':
    case 'returnwaiting':
    case 'tobeturned':
      return 'IadeEdilecek';
    case 'iadeedildi':
    case 'iade':
    case 'returned':
    case 'refunded':
      return 'IadeEdildi';
    case 'iptal':
    case 'iptaledildi':
    case 'cancelled':
    case 'canceled':
      return 'Iptal';
    default:
      return 'Kaydedildi';
  }
}

const BASE_STATUS_LABELS: Record<TicketStatus, string> = {
  Kaydedildi: 'Kayıt Alındı',
  Incelemede: 'Arıza Tespiti',
  TestEdiliyor: 'Test Ediliyor',
  Sivida: 'Sıvıda',
  ParcaBekliyor: 'Parça Bekliyor',
  AltServiste: 'Alt Serviste',
  MusteriOnayi: 'Onay Bekliyor',
  Onaylandi: 'Onaylandı',
  Onaylanmadi: 'Onaylanmadı',
  Tamirde: 'Onarımda',
  Hazir: 'Teslimata Hazır',
  TeslimEdildi: 'Teslim Edildi',
  IadeEdilecek: 'İade Edilecek',
  IadeEdildi: 'İade Edildi',
  Iptal: 'İptal Edildi',
};

const BASE_STATUS_COLORS: Record<TicketStatus, { bg: string; text: string; border: string }> = {
  Kaydedildi: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  Incelemede: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  TestEdiliyor: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
  Sivida: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  ParcaBekliyor: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  AltServiste: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  MusteriOnayi: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  Onaylandi: { bg: 'bg-teal-50', text: 'text-teal-800', border: 'border-teal-300' },
  Onaylanmadi: { bg: 'bg-rose-50', text: 'text-rose-800', border: 'border-rose-300' },
  Tamirde: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  Hazir: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  TeslimEdildi: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
  IadeEdilecek: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  IadeEdildi: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
  Iptal: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' },
};

export const STATUS_LABELS: Record<TicketStatus, string> = new Proxy(BASE_STATUS_LABELS as any, {
  get(target, prop: string) {
    if (typeof prop === 'string' && prop in target) {
      return target[prop];
    }
    const norm = normalizeTicketStatus(prop);
    if (norm in target) {
      return target[norm];
    }
    return String(prop || 'Kayıt Alındı');
  },
});

export const STATUS_COLORS: Record<TicketStatus, { bg: string; text: string; border: string }> = new Proxy(BASE_STATUS_COLORS as any, {
  get(target, prop: string) {
    if (typeof prop === 'string' && prop in target) {
      return target[prop];
    }
    const norm = normalizeTicketStatus(prop);
    if (norm in target) {
      return target[norm];
    }
    return DEFAULT_STATUS_COLOR;
  },
});

export function getStatusColors(status?: any): { bg: string; text: string; border: string } {
  const norm = normalizeTicketStatus(status);
  return BASE_STATUS_COLORS[norm] || DEFAULT_STATUS_COLOR;
}

export function getStatusLabel(status?: any): string {
  const norm = normalizeTicketStatus(status);
  return BASE_STATUS_LABELS[norm] || String(status || 'Kayıt Alındı');
}

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  Ucretli: 'Ücretli Servis',
  Garantili: 'Garantili',
  ServisGarantili: 'Servis Garantili (İş Takibi)',
  Sozlesmeli: 'Sözleşmeli Kurumsal Firma',
  DisServis: 'Dış Servis',
};

export const SERVICE_TYPE_DESCRIPTIONS: Record<ServiceType, string> = {
  Ucretli: 'Standart müşteri ücretli servis kaydı',
  Garantili: 'Garanti kapsamındaki cihaz onarımı',
  ServisGarantili: 'Daha önce servisimizde yapılmış aynı arıza geri gelmiş / garanti içi iş takibi',
  Sozlesmeli: 'Anlaşmalı cari firma bakım-onarım sözleşmesi (Sözleşme bakiyesinden düşer, ana kasaya sözleşme bedeli işler)',
  DisServis: 'Şehir dışında yerinde onarım için yapılan servis ve yol giderleri',
};

export const SERVICE_TYPE_COLORS: Record<ServiceType, { bg: string; text: string; border: string; badgeBg: string; lightBg: string }> = {
  Ucretli: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', badgeBg: 'bg-blue-600', lightBg: 'bg-blue-50/70' },
  Garantili: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', badgeBg: 'bg-emerald-600', lightBg: 'bg-emerald-50/70' },
  ServisGarantili: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', badgeBg: 'bg-amber-600', lightBg: 'bg-amber-50/70' },
  Sozlesmeli: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', badgeBg: 'bg-purple-600', lightBg: 'bg-purple-50/70' },
  DisServis: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', badgeBg: 'bg-cyan-600', lightBg: 'bg-cyan-50/70' },
};

export function calculateTicketTotal(
  parts: { unitPrice: number; quantity: number; unitPriceUSD?: number; currency?: CurrencyCode; exchangeRate?: number }[] = [],
  laborCost: number = 0,
  vatRate: number = 0,
  discount: number = 0,
  _isVatIncluded: boolean = false,
  roundingAdjustment: number = 0
): { partsTotal: number; subtotal: number; vatAmount: number; grandTotal: number; baseSubtotal: number } {
  const safeParts = Array.isArray(parts) ? parts : [];
  const partsTotal = safeParts.reduce((sum, p) => sum + getPartPriceTL(p) * (p?.quantity || 1), 0);
  const safeLaborGross = Math.max(0, Number(laborCost) || 0);
  // Sistem kuralı: KDV oranı sabit %20. Eski %0/%10 kayıtları hesabı değiştirmez.
  const safeVatRate = 20;
  const vatMultiplier = 1 + safeVatRate / 100;

  // Yeni fiyatlandırma kuralı:
  // - Parça satış fiyatları her zaman KDV HARİÇ girilir ve üzerine KDV eklenir.
  // - İşçilik tutarı her zaman KDV DAHİL girilir; tekrar KDV eklenmez.
  // - İskonto, KDV hesaplandıktan sonraki ANA/GENEL TOPLAMDAN düşülür.
  // Eski isVatIncluded parametresi veri uyumluluğu için imzada tutulur ancak artık toplam hesabını değiştirmez.
  const laborNet = safeVatRate > 0 ? safeLaborGross / vatMultiplier : safeLaborGross;
  const netBeforeDiscount = partsTotal + laborNet;
  const grossBeforeDiscount = safeVatRate > 0 ? netBeforeDiscount * vatMultiplier : netBeforeDiscount;

  const requestedDiscount = Math.max(0, Number(discount) || 0);
  const appliedDiscount = Math.min(requestedDiscount, grossBeforeDiscount);
  const afterDiscount = Math.max(0, grossBeforeDiscount - appliedDiscount);

  // Otomatik yuvarlama: nihai tutarı en yakın 10 TL'ye getirir.
  // Örnek: 1847,17 -> 1850,00 | 1843,17 -> 1840,00
  // Eski manuel yuvarlama değeri hesabı ikinci kez etkilemez.
  const grandTotal = Math.round(afterDiscount / 10) * 10;

  // Rapor/PDF KDV matrahı nihai toplamla tutarlı kalsın.
  const subtotal = safeVatRate > 0 ? grandTotal / vatMultiplier : grandTotal;
  const vatAmount = safeVatRate > 0 ? grandTotal - subtotal : 0;

  return { partsTotal, subtotal, vatAmount, grandTotal, baseSubtotal: subtotal };
}

export function calculateTicketPartsCost(
  parts: { costPrice?: number; unitPrice?: number; quantity: number; costPriceUSD?: number; unitPriceUSD?: number; currency?: CurrencyCode; exchangeRate?: number }[] = []
): number {
  const safeParts = Array.isArray(parts) ? parts : [];
  return safeParts.reduce((sum, p) => sum + getPartCostTL(p) * (p?.quantity || 1), 0);
}

export function calculateTicketTotalCost(ticket: { parts?: ServicePart[]; subServiceCost?: number }): number {
  const partsCost = calculateTicketPartsCost(ticket.parts || []);
  const subCost = typeof ticket.subServiceCost === 'number' && !isNaN(ticket.subServiceCost) ? ticket.subServiceCost : 0;
  return partsCost + subCost;
}

export function getTicketGrandTotal(ticket: ServiceTicket): number {
  if (ticket.totalAmount !== undefined && ticket.totalAmount > 0) {
    return ticket.totalAmount;
  }
  const { grandTotal } = calculateTicketTotal(
    ticket.parts || [],
    ticket.laborCost || 0,
    ticket.vatRate ?? 20,
    ticket.discount || 0,
    ticket.isVatIncluded || false
  );
  return grandTotal;
}

export function getTicketReportDate(ticket: ServiceTicket): string {
  // Servis geliri, kaydın açıldığı veya teslim edildiği aya değil son işlem
  // tarihine yazılır. Eski kayıtlarda son güncelleme alanı yoksa geriye dönük
  // uyumluluk için teslim ve kayıt tarihleri kullanılır.
  return ticket.updatedAt || ticket.lastUpdatedAt || ticket.deliveryDate || ticket.createdAt;
}

export function normalizePaymentMethod(method?: string): 'Nakit' | 'Kredi Karti' | 'EFT/Havale' {
  if (!method) return 'Nakit';
  const clean = method.toLowerCase().trim();
  if (clean.includes('kart') || clean.includes('pos') || clean.includes('card') || clean.includes('kredikarti')) {
    return 'Kredi Karti';
  }
  if (clean.includes('havale') || clean.includes('eft') || clean.includes('fast') || clean.includes('bank') || clean.includes('transfer') || clean.includes('havaleeft')) {
    return 'EFT/Havale';
  }
  return 'Nakit';
}

export function getTicketPaymentSplit(t: ServiceTicket): { cash: number; card: number; transfer: number; total: number } {
  const isContracted = t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId);
  const outOfContractParts = (t.parts || []).filter(p => p.isOutOfContract === true);
  const hasOutOfContractParts = outOfContractParts.length > 0;

  // Sözleşmeli kayıt ve sözleşme harici ücretli parça YOKSA:
  // Bu servis doğrudan sözleşmeden karşılanır, kasa ödeme alanları 0 kalır (kasa gelirine nakit/kart eklenmez).
  if (isContracted && !hasOutOfContractParts) {
    return { cash: 0, card: 0, transfer: 0, total: 0 };
  }

  const outOfContractTotal = hasOutOfContractParts
    ? calculateTicketTotal(outOfContractParts, 0, t.vatRate || 0, 0, false).grandTotal
    : 0;

  const { grandTotal } = calculateTicketTotal(
    t.parts || [],
    t.laborCost || 0,
    t.vatRate || 0,
    t.discount || 0,
    t.isVatIncluded || false
  );

  const targetPayable = isContracted ? outOfContractTotal : grandTotal;

  let totalPaid = 0;
  if (t.paymentStatus === 'Odendi') {
    totalPaid = t.paidAmount !== undefined && t.paidAmount > 0 ? t.paidAmount : targetPayable;
  } else if (t.paymentStatus === 'KismiOdendi') {
    totalPaid = t.paidAmount || 0;
  } else if (t.paidAmount !== undefined && t.paidAmount > 0) {
    totalPaid = t.paidAmount;
  } else if (t.payments && Array.isArray(t.payments) && t.payments.length > 0) {
    totalPaid = t.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  }

  if (totalPaid <= 0) {
    return { cash: 0, card: 0, transfer: 0, total: 0 };
  }

  // 1. If explicit payments array exists with items
  if (t.payments && Array.isArray(t.payments) && t.payments.length > 0) {
    let cash = 0;
    let card = 0;
    let transfer = 0;
    t.payments.forEach(p => {
      const amt = Number(p.amount) || 0;
      if (amt <= 0) return;
      const m = normalizePaymentMethod(p.method);
      if (m === 'Kredi Karti') card += amt;
      else if (m === 'EFT/Havale') transfer += amt;
      else cash += amt;
    });
    const sum = cash + card + transfer;
    if (sum > 0) {
      return { cash, card, transfer, total: sum };
    }
  }

  // 2. If explicit paymentBreakdown exists
  if (t.paymentBreakdown && typeof t.paymentBreakdown === 'object') {
    const cash = Math.max(0, Number(t.paymentBreakdown.cash) || 0);
    const card = Math.max(0, Number(t.paymentBreakdown.card) || 0);
    const transfer = Math.max(0, Number(t.paymentBreakdown.transfer) || 0);
    const sum = cash + card + transfer;
    if (sum > 0) {
      return { cash, card, transfer, total: sum };
    }
  }

  // 3. Fallback to single paymentMethod
  const method = normalizePaymentMethod(t.paymentMethod);
  if (method === 'Kredi Karti') {
    return { cash: 0, card: totalPaid, transfer: 0, total: totalPaid };
  } else if (method === 'EFT/Havale') {
    return { cash: 0, card: 0, transfer: totalPaid, total: totalPaid };
  }
  return { cash: totalPaid, card: 0, transfer: 0, total: totalPaid };
}

/**
 * Returns only the money that was actually collected in the requested day/month.
 * Status, delivery and updatedAt dates are intentionally ignored: editing or
 * delivering a paid ticket must never create a second cash movement.
 */
export function getTicketPaymentSplitForPeriod(
  t: ServiceTicket,
  periodKey: string
): { cash: number; card: number; transfer: number; total: number } {
  const result = { cash: 0, card: 0, transfer: 0, total: 0 };
  const paymentRecords = Array.isArray(t.payments) ? t.payments : [];

  if (paymentRecords.length > 0) {
    paymentRecords.forEach(p => {
      if (!p.date || !p.date.startsWith(periodKey)) return;
      const amount = Math.max(0, Number(p.amount) || 0);
      if (amount <= 0) return;
      const method = normalizePaymentMethod(p.method);
      if (method === 'Kredi Karti') result.card += amount;
      else if (method === 'EFT/Havale') result.transfer += amount;
      else result.cash += amount;
    });
    result.total = result.cash + result.card + result.transfer;
    return result;
  }

  // Compatibility for old records created before the payment history array.
  // Only their explicit paymentDate is accepted; updatedAt is never a payment date.
  if (t.paymentDate && t.paymentDate.startsWith(periodKey)) {
    return getTicketPaymentSplit(t);
  }

  return result;
}

export function formatTicketPaymentMethodDisplay(t: ServiceTicket): string {
  const isContracted = t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId);
  const outOfContractParts = (t.parts || []).filter(p => p.isOutOfContract === true);
  const hasOutOfContractParts = outOfContractParts.length > 0;

  if (isContracted) {
    if (!hasOutOfContractParts) {
      return 'Sözleşme Kapsamında (Ödendi)';
    }
    const split = getTicketPaymentSplit(t);
    if (split.total <= 0) {
      return 'Harici Ödeme Bekliyor';
    }
    const activeMethods: string[] = [];
    if (split.cash > 0) activeMethods.push(`Harici Nakit (${formatTL(split.cash)})`);
    if (split.card > 0) activeMethods.push(`Harici Kart (${formatTL(split.card)})`);
    if (split.transfer > 0) activeMethods.push(`Harici EFT (${formatTL(split.transfer)})`);
    if (activeMethods.length > 1) {
      return `Harici Karma (${activeMethods.join(' + ')})`;
    }
    if (activeMethods.length === 1) {
      return activeMethods[0];
    }
    return 'Harici Ödendi';
  }

  const split = getTicketPaymentSplit(t);
  if (split.total <= 0) {
    return t.paymentMethod || 'Ödenmedi / Bekliyor';
  }

  const activeMethods: string[] = [];
  if (split.cash > 0) activeMethods.push(`Nakit (${formatTL(split.cash)})`);
  if (split.card > 0) activeMethods.push(`Kredi Kartı (${formatTL(split.card)})`);
  if (split.transfer > 0) activeMethods.push(`EFT/Havale (${formatTL(split.transfer)})`);

  if (activeMethods.length > 1) {
    return `Karma / Parçalı (${activeMethods.join(' + ')})`;
  }
  if (activeMethods.length === 1) {
    if (split.cash > 0) return 'Nakit';
    if (split.card > 0) return 'Kredi Kartı';
    if (split.transfer > 0) return 'EFT / Havale';
  }
  return t.paymentMethod || 'Nakit';
}

export interface PaymentMethodBreakdown {
  cash: { amount: number; count: number; percentage: number };
  card: { amount: number; count: number; percentage: number };
  transfer: { amount: number; count: number; percentage: number };
  totalCollected: number;
}

export function calculatePaymentBreakdown(tickets: ServiceTicket[]): PaymentMethodBreakdown {
  let cashAmount = 0;
  let cashCount = 0;
  let cardAmount = 0;
  let cardCount = 0;
  let transferAmount = 0;
  let transferCount = 0;
  let totalCollected = 0;

  tickets.forEach(t => {
    const split = getTicketPaymentSplit(t);

    if (split.total > 0) {
      totalCollected += split.total;

      if (split.cash > 0) {
        cashAmount += split.cash;
        cashCount++;
      }
      if (split.card > 0) {
        cardAmount += split.card;
        cardCount++;
      }
      if (split.transfer > 0) {
        transferAmount += split.transfer;
        transferCount++;
      }
    }
  });

  const cashPercent = totalCollected > 0 ? Math.round((cashAmount / totalCollected) * 100) : 0;
  const cardPercent = totalCollected > 0 ? Math.round((cardAmount / totalCollected) * 100) : 0;
  const transferPercent = totalCollected > 0 ? Math.max(0, 100 - cashPercent - cardPercent) : 0;

  return {
    cash: { amount: cashAmount, count: cashCount, percentage: cashPercent },
    card: { amount: cardAmount, count: cardCount, percentage: cardPercent },
    transfer: { amount: transferAmount, count: transferCount, percentage: transferPercent },
    totalCollected,
  };
}

export function calculatePaymentBreakdownForPeriod(tickets: ServiceTicket[], periodKey: string): PaymentMethodBreakdown {
  let cashAmount = 0;
  let cashCount = 0;
  let cardAmount = 0;
  let cardCount = 0;
  let transferAmount = 0;
  let transferCount = 0;
  let totalCollected = 0;

  tickets.forEach(ticket => {
    const split = getTicketPaymentSplitForPeriod(ticket, periodKey);
    totalCollected += split.total;
    if (split.cash > 0) { cashAmount += split.cash; cashCount++; }
    if (split.card > 0) { cardAmount += split.card; cardCount++; }
    if (split.transfer > 0) { transferAmount += split.transfer; transferCount++; }
  });

  const cashPercent = totalCollected > 0 ? Math.round((cashAmount / totalCollected) * 100) : 0;
  const cardPercent = totalCollected > 0 ? Math.round((cardAmount / totalCollected) * 100) : 0;
  const transferPercent = totalCollected > 0 ? Math.max(0, 100 - cashPercent - cardPercent) : 0;
  return {
    cash: { amount: cashAmount, count: cashCount, percentage: cashPercent },
    card: { amount: cardAmount, count: cardCount, percentage: cardPercent },
    transfer: { amount: transferAmount, count: transferCount, percentage: transferPercent },
    totalCollected,
  };
}

export function isTicketAssignedToUser(ticket: ServiceTicket, user: AppUser | null | undefined): boolean {
  if (!user) return false;

  // Personel performans/kazanç raporu işi gerçekten yapan teknisyene aittir.
  // Teslim eden kullanıcı bu eşleşmeye kesinlikle dahil edilmez.
  const hasRepairTechnician = Boolean(ticket.repairedByUser || ticket.repairedByName);
  if (hasRepairTechnician) {
    if (ticket.repairedByUser && ticket.repairedByUser.toLowerCase() === user.username.toLowerCase()) return true;
    if (ticket.repairedByName && ticket.repairedByName.toLowerCase() === user.name.toLowerCase()) return true;
    return false;
  }

  // Eski kayıtlarda repairedBy bulunmayabilir; yalnız bu kayıtlar için atanmış
  // teknisyen geriye dönük yedek eşleşme olarak kullanılır.
  if (ticket.assignedTechnicianId && ticket.assignedTechnicianId === user.id) return true;
  if (ticket.assignedTechnicianUsername && ticket.assignedTechnicianUsername.toLowerCase() === user.username.toLowerCase()) return true;
  if (ticket.assignedTechnicianName && ticket.assignedTechnicianName.toLowerCase() === user.name.toLowerCase()) return true;
  if (!ticket.assignedTechnicianId && !ticket.assignedTechnicianUsername && ticket.createdByUser && ticket.createdByUser.toLowerCase() === user.username.toLowerCase()) return true;
  return false;
}

export function getTicketAssignedTechnicianDisplay(ticket: ServiceTicket, users?: AppUser[]): string {
  if (ticket.repairedByName) return ticket.repairedByName;
  if (ticket.repairedByUser) {
    const found = users?.find(u => u.username.toLowerCase() === ticket.repairedByUser?.toLowerCase());
    if (found) return found.name;
    return ticket.repairedByUser;
  }
  if (ticket.assignedTechnicianName) return ticket.assignedTechnicianName;
  if (ticket.assignedTechnicianUsername) {
    const found = users?.find(u => u.username.toLowerCase() === ticket.assignedTechnicianUsername?.toLowerCase());
    if (found) return found.name;
    return ticket.assignedTechnicianUsername;
  }
  if (ticket.assignedTechnicianId) {
    const found = users?.find(u => u.id === ticket.assignedTechnicianId);
    if (found) return found.name;
  }
  if (ticket.createdByName) return ticket.createdByName;
  if (ticket.createdByUser) return ticket.createdByUser;
  return 'Atanmadı';
}

// ----------------------------------------------------
// CONTRACTED COMPANIES (SÖZLEŞMELİ FİRMALAR) ENGINE
// ----------------------------------------------------

export const defaultDemoContractedCompanies: ContractedCompany[] = [];

export function getStoredContractedCompanies(): ContractedCompany[] {
  try {
    const raw = localStorage.getItem(CONTRACTED_COMPANIES_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return [];
  } catch (err) {
    console.error('Error loading contracted companies from storage:', err);
    return [];
  }
}

export function saveContractedCompanies(companies: ContractedCompany[]): void {
  try {
    const safeCompanies = Array.isArray(companies) ? companies : [];
    localStorage.setItem(CONTRACTED_COMPANIES_KEY, JSON.stringify(safeCompanies));
    syncTrackedEntity('contractedCompanies', { contractedCompanies: safeCompanies }).catch(() => {});
  } catch (err) {
    console.error('Error saving contracted companies:', err);
  }
}

export function generateNextContractCompanyId(companies: ContractedCompany[]): string {
  const prefix = 'CC-';
  let maxNum = 1000;
  companies.forEach(c => {
    if (c.id && c.id.startsWith(prefix)) {
      const numPart = parseInt(c.id.replace(prefix, ''), 10);
      if (!isNaN(numPart) && numPart > maxNum) {
        maxNum = numPart;
      }
    }
  });
  return `${prefix}${maxNum + 1}`;
}

export interface ContractCompanyMetrics {
  ticketsCount: number;
  companyTickets: ServiceTicket[];
  totalServicesListPrice: number; // Sözleşme dahilindeki servislerin liste fiyatı toplamı (Bakiyeden düşen)
  totalPartsCost: number; // Kullanılan tüm parçaların maliyeti
  inContractPartsCost: number; // Sözleşme içi parça maliyeti
  outOfContractPartsCost: number; // Sözleşme harici ekstra parça maliyeti
  totalSubServiceCost: number; // Varsa alt servis dış maliyeti
  totalDirectCost: number; // Gerçek harcanan toplam maliyet (Parça + Alt Servis)
  totalContractFeePaid: number; // Yapılan sözleşme tahsilatları toplamı
  effectiveContractRevenue: number; // Dönem sözleşme bedeli
  remainingBalance: number; // Kalan sözleşme bakiyesi (Sözleşme bedeli - Sözleşme içi servisler)
  isBalanceExceeded: boolean; // Bakiye aşıldı mı?
  exceededAmount: number; // Aşılan tutar
  extraOutOfContractRevenue: number; // Sözleşme harici ekstra parça tahsilat geliri
  extraOutOfContractProfit: number; // Sözleşme harici ekstra parça kârı
  netProfit: number; // Gerçek Kâr (Sözleşme Net Kârı + Ekstra Parça Kârı)
  profitMargin: number; // Kâr marjı %
  utilizationPercent: number; // Kullanım / Hak Ediş Oranı %
  healthStatus: 'healthy' | 'warning' | 'critical';
}

export function getTicketContractBreakdown(ticket: ServiceTicket): {
  inContractParts: ServicePart[];
  outOfContractParts: ServicePart[];
  inContractGrandTotal: number;
  outOfContractTotal: number;
  outOfContractCost: number;
  hasOutOfContractParts: boolean;
  extraDebt: number;
  effectiveDebt: number;
} {
  const parts = ticket.parts || [];
  const inContractParts = parts.filter(p => !p.isOutOfContract);
  const outOfContractParts = parts.filter(p => p.isOutOfContract === true);

  const { grandTotal: inContractGrandTotal } = calculateTicketTotal(
    inContractParts,
    ticket.laborCost || 0,
    ticket.vatRate ?? 20,
    ticket.discount || 0,
    ticket.isVatIncluded || false
  );

  const outOfContractTotal = calculateTicketTotal(
    outOfContractParts,
    0,
    ticket.vatRate ?? 20,
    0,
    false
  ).grandTotal;
  const outOfContractCost = calculateTicketPartsCost(outOfContractParts);
  const hasOutOfContractParts = outOfContractParts.length > 0;
  const isContracted = ticket.serviceType === 'Sozlesmeli' || Boolean(ticket.contractedCompanyId);

  if (isContracted) {
    // Sözleşmeli kayıtlarda sözleşme kapsamındaki bakiye cari borç olarak gözükmez (otomatik mahsup / ödendi).
    // Yalnızca sözleşme harici ekstra parça varsa ve ödenmediyse harici borç/tahsilat olarak yansır.
    if (!hasOutOfContractParts) {
      return {
        inContractParts,
        outOfContractParts,
        inContractGrandTotal,
        outOfContractTotal: 0,
        outOfContractCost: 0,
        hasOutOfContractParts: false,
        extraDebt: 0,
        effectiveDebt: 0,
      };
    }

    const splitPaid = ticket.paymentBreakdown
      ? (ticket.paymentBreakdown.cash || 0) + (ticket.paymentBreakdown.card || 0) + (ticket.paymentBreakdown.transfer || 0)
      : 0;
    const paid = ticket.paymentStatus === 'Odendi'
      ? (ticket.paidAmount && ticket.paidAmount > 0 ? ticket.paidAmount : (splitPaid > 0 ? splitPaid : outOfContractTotal))
      : (splitPaid > 0 ? splitPaid : (ticket.paidAmount || 0));

    const extraDebt = Math.max(0, outOfContractTotal - paid);
    return {
      inContractParts,
      outOfContractParts,
      inContractGrandTotal,
      outOfContractTotal,
      outOfContractCost,
      hasOutOfContractParts: true,
      extraDebt,
      effectiveDebt: extraDebt,
    };
  }

  // Standart (Ücretli, Garantili vb.) kayıtlar
  const { grandTotal } = calculateTicketTotal(
    parts,
    ticket.laborCost || 0,
    ticket.vatRate ?? 20,
    ticket.discount || 0,
    ticket.isVatIncluded || false
  );
  const paid =
    ticket.paymentStatus === 'Odendi'
      ? (ticket.paidAmount !== undefined && ticket.paidAmount > 0 ? ticket.paidAmount : grandTotal)
      : ticket.paymentStatus === 'KismiOdendi'
      ? ticket.paidAmount || 0
      : 0;
  const effectiveDebt = Math.max(0, grandTotal - paid);

  return {
    inContractParts: parts,
    outOfContractParts: [],
    inContractGrandTotal: grandTotal,
    outOfContractTotal: 0,
    outOfContractCost: 0,
    hasOutOfContractParts: false,
    extraDebt: 0,
    effectiveDebt,
  };
}

export function calculateContractCompanyMetrics(
  company: ContractedCompany,
  tickets: ServiceTicket[],
  periodMonth?: string // Format: 'YYYY-MM'
): ContractCompanyMetrics {
  // 1. Filter tickets that belong to this company
  const companyTickets = tickets.filter(t => {
    const isThisCompany =
      t.contractedCompanyId === company.id ||
      (t.serviceType === 'Sozlesmeli' && t.contractedCompanyName?.trim().toLowerCase() === company.name.trim().toLowerCase());
    if (!isThisCompany) return false;
    if (periodMonth) {
      const tDate = t.createdAt || t.deliveryDate || '';
      return tDate.startsWith(periodMonth);
    }
    return true;
  });

  // 2. Calculate service totals (Separating in-contract vs out-of-contract extra parts)
  let totalServicesListPrice = 0; // Only in-contract services deduct from the contract balance
  let totalPartsCost = 0;
  let inContractPartsCost = 0;
  let outOfContractPartsCost = 0;
  let extraOutOfContractRevenue = 0;
  let totalSubServiceCost = 0;

  companyTickets.forEach(t => {
    const breakdown = getTicketContractBreakdown(t);
    totalServicesListPrice += breakdown.inContractGrandTotal;

    const ticketInCost = calculateTicketPartsCost(breakdown.inContractParts);
    const ticketOutCost = breakdown.outOfContractCost;

    inContractPartsCost += ticketInCost;
    outOfContractPartsCost += ticketOutCost;
    totalPartsCost += ticketInCost + ticketOutCost;
    extraOutOfContractRevenue += breakdown.outOfContractTotal;

    totalSubServiceCost += t.subServiceCost || 0;
  });

  const totalDirectCost = totalPartsCost + totalSubServiceCost;
  const extraOutOfContractProfit = extraOutOfContractRevenue - outOfContractPartsCost;

  // 3. Calculate contract fee collected
  let totalContractFeePaid = 0;
  if (company.payments && Array.isArray(company.payments)) {
    company.payments.forEach(p => {
      if (!periodMonth || (p.date && p.date.startsWith(periodMonth)) || (p.periodMonth && p.periodMonth === periodMonth)) {
        totalContractFeePaid += Number(p.amount) || 0;
      }
    });
  }

  // Effective revenue is contract fee collected or default monthly fee if active
  const effectiveContractRevenue = totalContractFeePaid > 0 ? totalContractFeePaid : (company.monthlyFee || 0);

  // Remaining balance (Sözleşme harici parçalar sözleşme bakiyesinden DEĞİL harici tahsilat olarak sayılır)
  const remainingBalance = Math.max(0, effectiveContractRevenue - totalServicesListPrice);
  const isBalanceExceeded = totalServicesListPrice > effectiveContractRevenue;
  const exceededAmount = isBalanceExceeded ? totalServicesListPrice - effectiveContractRevenue : 0;

  // Net Profit = (Contract Fee - In-Contract Direct Costs) + Extra Out-of-Contract Parts Profit
  const inContractDirectCost = inContractPartsCost + totalSubServiceCost;
  const contractNetProfit = effectiveContractRevenue - inContractDirectCost;
  const netProfit = contractNetProfit + extraOutOfContractProfit;

  const totalAllRevenue = effectiveContractRevenue + extraOutOfContractRevenue;
  const profitMargin = totalAllRevenue > 0 ? (netProfit / totalAllRevenue) * 100 : 0;
  const utilizationPercent = effectiveContractRevenue > 0 ? (totalServicesListPrice / effectiveContractRevenue) * 100 : 0;

  let healthStatus: 'healthy' | 'warning' | 'critical' = 'healthy';
  if (isBalanceExceeded || netProfit < 0) {
    healthStatus = 'critical'; // Bakiye aşıldı veya zarar
  } else if (utilizationPercent >= 80) {
    healthStatus = 'warning'; // Bakiyenin %80'i veya fazlası tüketildi
  }

  return {
    ticketsCount: companyTickets.length,
    companyTickets,
    totalServicesListPrice,
    totalPartsCost,
    inContractPartsCost,
    outOfContractPartsCost,
    totalSubServiceCost,
    totalDirectCost,
    totalContractFeePaid,
    effectiveContractRevenue,
    remainingBalance,
    isBalanceExceeded,
    exceededAmount,
    extraOutOfContractRevenue,
    extraOutOfContractProfit,
    netProfit,
    profitMargin,
    utilizationPercent,
    healthStatus,
  };
}

export function getFullDatabaseData() {
  const tickets = getStoredTickets();
  const company = getStoredCompanyInfo();
  const deviceTypes = getStoredDeviceTypes();
  const deviceBrands = getStoredDeviceBrands();
  const inventory = getStoredInventory();
  const inventoryCategories = getStoredInventoryCategories();
  const stockMovements = getStoredStockMovements();
  const users = getStoredUsers();
  const contractedCompanies = getStoredContractedCompanies();
  const exchangeRate = getStoredExchangeRate();

  let labelConfig = null;
  try {
    const raw = localStorage.getItem('service_custom_label_config_v2');
    if (raw) labelConfig = JSON.parse(raw);
  } catch (_) {}

  let smsSettings = null;
  try {
    const raw = localStorage.getItem('tekservis_sms_settings');
    if (raw) smsSettings = JSON.parse(raw);
  } catch (_) {}

  let smsLogs: any[] = [];
  try {
    const raw = localStorage.getItem('tekservis_sms_logs');
    if (raw) smsLogs = JSON.parse(raw);
  } catch (_) {}

  return {
    company,
    users,
    deviceTypes,
    deviceBrands,
    inventoryCategories,
    inventory,
    stockMovements,
    tickets,
    contractedCompanies,
    labelConfig,
    smsSettings,
    smsLogs,
    exchangeRate,
  };
}

export function downloadServerDataJson() {
  const fullData = getFullDatabaseData();
  const blob = new Blob([JSON.stringify(fullData, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `teknik_servis_yedek_${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function restoreFullDatabaseData(imported: any): Promise<{ success: boolean; message: string; count?: number }> {
  try {
    if (!imported || typeof imported !== 'object') {
      return { success: false, message: 'Geçersiz yedek dosyası yapısı.' };
    }

    // 1. Tickets
    if (Array.isArray(imported.tickets)) {
      saveTickets(imported.tickets);
    }

    // 2. Inventory
    if (Array.isArray(imported.inventory)) {
      saveInventory(imported.inventory);
    }

    // 3. Stock Movements
    if (Array.isArray(imported.stockMovements)) {
      saveStockMovements(imported.stockMovements);
    }

    // 4. Contracted Companies
    if (Array.isArray(imported.contractedCompanies)) {
      saveContractedCompanies(imported.contractedCompanies);
    }

    // 5. Device Brands & Types & Categories
    if (Array.isArray(imported.deviceBrands)) {
      saveDeviceBrands(imported.deviceBrands);
    }
    if (Array.isArray(imported.deviceTypes)) {
      saveDeviceTypes(imported.deviceTypes);
    }
    if (Array.isArray(imported.inventoryCategories)) {
      saveInventoryCategories(imported.inventoryCategories);
    }

    // 6. Users
    if (Array.isArray(imported.users)) {
      saveUsers(imported.users);
    }

    // 7. Company info
    if (imported.company && typeof imported.company === 'object') {
      saveCompanyInfo(imported.company);
    }

    // 8. SMS Settings & Logs
    if (imported.smsSettings) {
      try {
        localStorage.setItem('tekservis_sms_settings', JSON.stringify(imported.smsSettings));
      } catch (_) {}
    }
    if (Array.isArray(imported.smsLogs)) {
      try {
        localStorage.setItem('tekservis_sms_logs', JSON.stringify(imported.smsLogs));
      } catch (_) {}
    }

    // 9. Label Config
    if (imported.labelConfig) {
      try {
        localStorage.setItem('service_custom_label_config_v2', JSON.stringify(imported.labelConfig));
      } catch (_) {}
    }

    // 10. Sync full payload directly to Server API (Node & PHP)
    const payload = getFullDatabaseData();
    await fetch('/api/restore-backup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => null);

    await fetch('/api/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, replace_all_tickets: true }),
    }).catch(() => null);

    await fetch('/api.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, replace_all_tickets: true }),
    }).catch(() => null);

    return {
      success: true,
      message: `Yedek başarıyla yüklendi! (${(imported.tickets || []).length} servis kaydı, ${(imported.inventory || []).length} stok kalemi)`,
      count: (imported.tickets || []).length,
    };
  } catch (err: any) {
    return { success: false, message: 'Yedek geri yüklenirken hata oluştu: ' + (err.message || err) };
  }
}

// =========================================================================
// MYSQL DATABASE CONNECTION CLIENT UTILS
// =========================================================================
const DEFAULT_MYSQL_CONFIG: MySqlConfig = {
  serverMode: 'hostinger',
  host: 'localhost',
  port: 3306,
  database: '',
  user: '',
  password: '',
  ssl: false,
};

export async function getMySqlConfig(): Promise<MySqlConfig> {
  let cached: Partial<MySqlConfig> = {};
  try {
    const local = localStorage.getItem('tekservis_mysql_config');
    if (local) cached = JSON.parse(local) || {};
  } catch (_) {}

  try {
    const res = await fetch('/api.php?action=mysql-config', { cache: 'no-store' });
    if (res.ok) {
      const data = await res.json();
      if (data?.config) {
        const serverCfg: MySqlConfig = {
          ...DEFAULT_MYSQL_CONFIG,
          ...cached,
          ...data.config,
          password: '', // Parola tarayıcıya geri verilmez.
        };

        // Sunucudan geçici/eksik yanıt geldiyse daha önceki sağlam bilgileri silme.
        if (!serverCfg.database && cached.database) serverCfg.database = String(cached.database);
        if (!serverCfg.user && cached.user) serverCfg.user = String(cached.user);
        if (!serverCfg.host && cached.host) serverCfg.host = String(cached.host);

        try {
          localStorage.setItem('tekservis_mysql_config', JSON.stringify({ ...serverCfg, password: '' }));
        } catch (_) {}
        return serverCfg;
      }
    }
  } catch (_) {
    // Ağ/API kesintısında son bilinen yapılandırmayı koru.
  }

  return {
    ...DEFAULT_MYSQL_CONFIG,
    ...cached,
    host: String(cached.host || DEFAULT_MYSQL_CONFIG.host),
    database: String(cached.database || ''),
    user: String(cached.user || ''),
    port: Number(cached.port) || DEFAULT_MYSQL_CONFIG.port,
    password: '',
  };
}

export async function saveMySqlConfig(config: MySqlConfig): Promise<{ success: boolean; message: string; config?: MySqlConfig }> {
  try {
    const res = await fetch('/api.php?action=mysql-config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });

    let data: any = null;
    try { data = await res.json(); } catch (_) {}
    if (!res.ok || !data?.success) {
      return {
        success: false,
        message: data?.message || `MySQL ayarları sunucuya kaydedilemedi (HTTP ${res.status}). Mevcut bağlantı bilgileri korunmuştur.`,
      };
    }

    const safeConfig: MySqlConfig = {
      ...DEFAULT_MYSQL_CONFIG,
      ...config,
      ...(data.config || {}),
      password: '',
    };
    try {
      const oldInstance = localStorage.getItem(DATABASE_INSTANCE_KEY) || '';
      const newInstance = String(safeConfig.databaseInstance || '');
      if (newInstance && oldInstance && newInstance !== oldInstance) {
        clearSensitiveLocalCache();
      }
      if (newInstance) localStorage.setItem(DATABASE_INSTANCE_KEY, newInstance);
      localStorage.setItem('tekservis_mysql_config', JSON.stringify(safeConfig));
      window.dispatchEvent(new CustomEvent('tekservis-database-changed'));
    } catch (_) {}
    return { ...data, config: safeConfig };
  } catch (err: any) {
    return {
      success: false,
      message: 'Sunucuya ulaşılamadığı için MySQL ayarları değiştirilmedi. Mevcut bilgiler korunuyor: ' + (err.message || 'Ağ hatası'),
    };
  }
}

export async function testMySqlConnection(config?: Partial<MySqlConfig>): Promise<MySqlTestResult> {
  try {
    const res = await fetch('/api.php?action=mysql-test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.fromEntries(
        Object.entries(config || {}).filter(([key, value]) => key !== 'password' && value !== '' && value !== null && value !== undefined)
      )),
    });

    if (res.ok) {
      const data: MySqlTestResult = await res.json();
      return data;
    } else {
      const errorText = await res.text();
      return {
        success: false,
        message: 'Sunucu bağlantı testine yanıt veremedi (HTTP ' + res.status + ')',
        error: errorText,
        troubleshooting: 'PHP sunucusunda /api.php?action=mysql-test uç noktasının çalıştığından emin olun.',
      };
    }
  } catch (err: any) {
    return {
      success: false,
      message: 'Bağlantı isteği gönderilemedi: ' + err.message,
      error: err.message,
      errorCode: 'NETWORK_ERROR',
      troubleshooting: 'Tarayıcı ile PHP API arasında bağlantı kurulamadı. api.php dosyasının yayınlandığını ve PHP\'nin aktif olduğunu kontrol edin.',
    };
  }
}
